import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { LandingLayout } from '../components/layout/LandingLayout';
import LandingPage from '../components/landing-page';
import { setConfigData } from '../redux/slices/configData';
import SEO from '../components/seo';
import useGetLandingPage from '../api-manage/hooks/react-query/useGetLandingPage';
import { getCookie } from '../helper-functions/getCookie';
import SplashScreen from '../splash-screen';
import useSplashScreen from '../hooks/useSplashScreen';
import ComingSoon from '../components/ComingSoon';
import { isLaunchDatePassed } from '../utils/launchDate';

const HomePage = () => {
    const { data, refetch } = useGetLandingPage();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [configData, setConfigDataState] = useState(null);
    const [landingPageData, setLandingPageData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Use the global splash screen hook
    const { shouldShowSplash, handleDOMReady } = useSplashScreen();

    useEffect(() => {
    const fetchData = async () => {
      const language = getCookie('languageSetting') || 'en';

      // Default fallback data
      const defaultConfig = {
        business_name: '6ammart',
        base_urls: {
          business_logo_url: 'https://admin.alot263.com',
        },
        fav_icon: 'favicon.ico',
        maintenance_mode: false,
        loyalty_point_status: 1,
      };

      const defaultLandingPageData = {
        banner_section: [],
        featured_categories: [],
        featured_stores: [],
        popular_items: [],
      };

      try {
        // Fetch config data
        const configRes = await fetch(
          `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/api/v1/config`,
          {
            method: 'GET',
            headers: {
              'X-software-id': 33571750,
              'X-server': 'server',
              'X-localization': language,
              origin: process.env.REACT_APP_CLIENT_HOST_URL || 'https://alot263.com',
              'Content-Type': 'application/json',
            },
          }
        );

        let config = defaultConfig;
        if (configRes.ok) {
          try {
            config = await configRes.json();
          } catch (error) {
            console.error('Failed to parse config JSON:', error);
          }
        } else {
          console.error('Config API failed:', configRes.status, configRes.statusText);
        }

        // Fetch landing page data
        const landingPageRes = await fetch(
          `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/api/v1/react-landing-page`,
          {
            method: 'GET',
            headers: {
              'X-software-id': 33571750,
              'X-server': 'server',
              'X-localization': language,
              origin: process.env.REACT_APP_CLIENT_HOST_URL || 'https://alot263.com',
              'Content-Type': 'application/json',
            },
          }
        );

        let landingPageData = defaultLandingPageData;
        if (landingPageRes.ok) {
          try {
            landingPageData = await landingPageRes.json();
          } catch (error) {
            console.error('Failed to parse landing page JSON:', error);
          }
        } else {
          console.error('Landing page API failed:', landingPageRes.status, landingPageRes.statusText);
        }

        setConfigDataState(config);
        setLandingPageData(landingPageData);

        // Check maintenance mode or empty config
        if (Array.isArray(config) && config.length === 0) {
          navigate('/404');
        } else if (config?.maintenance_mode) {
          navigate('/maintainance');
        } else {
          dispatch(setConfigData(config));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setConfigDataState(defaultConfig);
        setLandingPageData(defaultLandingPageData);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (configData) {
      refetch();
    }
  }, [configData, refetch]);

  // Show splash screen only if it should be shown and we're still loading data
  if (shouldShowSplash || loading) {
    return <SplashScreen onReady={handleDOMReady} shouldShow={shouldShowSplash} />;
  }

  // Show coming soon page if launch date hasn't passed yet
  if (!isLaunchDatePassed()) {
    return (
      <>
        <CssBaseline />
        <SEO
          title={`Coming Soon - ${configData?.business_name || '6ammart'}`}
          image={`${configData?.base_urls?.business_logo_url}/${configData?.fav_icon}`}
          businessName={configData?.business_name || '6ammart'}
        />
        <ComingSoon 
          configData={configData}
          landingPageData={landingPageData}
        />
      </>
    );
  }

  return (
    <>
      <CssBaseline />
      <SEO
        title={configData?.business_name || '6ammart'}
        image={`${configData?.base_urls?.business_logo_url}/${configData?.fav_icon}`}
        businessName={configData?.business_name || '6ammart'}
      />
      <LandingLayout configData={configData}>
        <LandingPage
          configData={configData}
          landingPageData={landingPageData}
          data={data}
        />
      </LandingLayout>
    </>
  );
};

export default HomePage;